import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { ChevronLeft, ArrowRight, Lightbulb, Save } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { validateMinLength } from '@/lib/validation';
import { SkipButton } from '@/components/SkipButton';
import { InlineVoiceInput } from '@/components/VoiceInput';
import { OnboardingLayout, STARTUP_ONBOARDING_STEPS } from '@/components/OnboardingLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useProfileStore } from '@/lib/state/profile-store';

export default function QuestionnaireProblemScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { isDesktop } = useResponsive();
  const params = useLocalSearchParams<{
    name: string;
    tagline: string;
    industry: string;
    stage: string;
    teamSize: string;
    location: string;
    foundedYear: string;
    website: string;
    mode: string;
  }>();

  const isStandalone = params.mode === 'standalone';
  const profile = useProfileStore((s) => s.startupProfile);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);

  const [problem, setProblem] = useState(isStandalone && profile?.problem ? profile.problem : '');
  const [solution, setSolution] = useState(isStandalone && profile?.solution ? profile.solution : '');
  const [targetCustomer, setTargetCustomer] = useState(isStandalone && profile?.targetCustomer ? profile.targetCustomer : '');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateMinLength(problem, 20)) {
      newErrors.problem = 'Please describe the problem in at least 20 characters';
    }
    if (!validateMinLength(solution, 20)) {
      newErrors.solution = 'Please describe your solution in at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (!validate()) return;

    if (isStandalone && profile) {
      // Standalone mode: save directly to store and go back
      setStartupProfile({
        ...profile,
        problem: problem.trim(),
        solution: solution.trim(),
        targetCustomer: targetCustomer.trim() || undefined,
      });
      router.back();
      return;
    }

    router.push({
      pathname: '/(onboarding)/questionnaire-traction',
      params: {
        ...params,
        problem,
        solution,
        targetCustomer,
      },
    });
  };

  // Form content (shared between mobile and desktop)
  const formContent = (
    <View className="gap-5">
      {/* Problem */}
      <View>
        <View className="flex-row items-center justify-between mb-2">
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">
            The Problem <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <InlineVoiceInput
            onTranscript={(text) => {
              setProblem((prev) => (prev ? `${prev} ${text}` : text));
              if (errors.problem) setErrors((e) => ({ ...e, problem: '' }));
            }}
          />
        </View>
        <Text style={{ color: colors.text.tertiary }} className="text-xs mb-3">
          What pain point or challenge does your target market face?
        </Text>
        <TextInput
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: errors.problem ? colors.red.main : colors.border.light,
            borderRadius: 12,
            padding: 16,
            color: colors.text.primary,
            fontSize: 16,
            height: 120,
            textAlignVertical: 'top',
          }}
          placeholder="Describe the problem you're solving..."
          placeholderTextColor={colors.text.tertiary}
          value={problem}
          onChangeText={(text) => {
            setProblem(text);
            if (errors.problem) setErrors((e) => ({ ...e, problem: '' }));
          }}
          multiline
          numberOfLines={5}
        />
        <View className="flex-row justify-between mt-1">
          {errors.problem ? (
            <Text style={{ color: colors.red.main }} className="text-xs">
              {errors.problem}
            </Text>
          ) : (
            <View />
          )}
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            {problem.length} characters
          </Text>
        </View>
      </View>

      {/* Solution */}
      <View>
        <View className="flex-row items-center justify-between mb-2">
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">
            Your Solution <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <InlineVoiceInput
            onTranscript={(text) => {
              setSolution((prev) => (prev ? `${prev} ${text}` : text));
              if (errors.solution) setErrors((e) => ({ ...e, solution: '' }));
            }}
          />
        </View>
        <Text style={{ color: colors.text.tertiary }} className="text-xs mb-3">
          How does your product or service solve this problem?
        </Text>
        <TextInput
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: errors.solution ? colors.red.main : colors.border.light,
            borderRadius: 12,
            padding: 16,
            color: colors.text.primary,
            fontSize: 16,
            height: 120,
            textAlignVertical: 'top',
          }}
          placeholder="Describe your solution..."
          placeholderTextColor={colors.text.tertiary}
          value={solution}
          onChangeText={(text) => {
            setSolution(text);
            if (errors.solution) setErrors((e) => ({ ...e, solution: '' }));
          }}
          multiline
          numberOfLines={5}
        />
        <View className="flex-row justify-between mt-1">
          {errors.solution ? (
            <Text style={{ color: colors.red.main }} className="text-xs">
              {errors.solution}
            </Text>
          ) : (
            <View />
          )}
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            {solution.length} characters
          </Text>
        </View>
      </View>

      {/* Target Customer */}
      <View>
        <View className="flex-row items-center justify-between mb-2">
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">
            Target Customer
          </Text>
          <InlineVoiceInput
            onTranscript={(text) => {
              setTargetCustomer((prev) => (prev ? `${prev} ${text}` : text));
            }}
          />
        </View>
        <Text style={{ color: colors.text.tertiary }} className="text-xs mb-3">
          Who is your ideal customer? (Optional but recommended)
        </Text>
        <TextInput
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
            borderRadius: 12,
            padding: 16,
            color: colors.text.primary,
            fontSize: 16,
            height: 100,
            textAlignVertical: 'top',
          }}
          placeholder="e.g., Small business owners who struggle with..."
          placeholderTextColor={colors.text.tertiary}
          value={targetCustomer}
          onChangeText={setTargetCustomer}
          multiline
          numberOfLines={4}
        />
      </View>

      {/* Continue / Save Button */}
      <View className="mt-4">
        <AnimatedPressable
          onPress={handleContinue}
          className="rounded-xl py-4 flex-row items-center justify-center gap-2"
          style={{ backgroundColor: colors.primary[600] }}
        >
          {isStandalone ? (
            <>
              <Save size={20} color={colors.text.inverse} />
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Save
              </Text>
            </>
          ) : (
            <>
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Continue
              </Text>
              <ArrowRight size={20} color={colors.text.inverse} />
            </>
          )}
        </AnimatedPressable>
      </View>
    </View>
  );

  // Desktop layout with OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        // Enhance-Profile screen, not part of the canonical onboarding flow —
        // no currentStep id to highlight (see src/lib/onboarding/steps.ts).
        steps={STARTUP_ONBOARDING_STEPS}
        title="Problem & Solution"
        subtitle="What problem are you solving?"
        userType="startup"
        entityName={params.name}
      >
        {formContent}
      </OnboardingLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          {/* Header */}
          <Animated.View
            entering={FadeInDown.delay(100).duration(500)}
            className="px-5 pt-4 pb-4"
          >
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => router.back()}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <View className="flex-row gap-2">
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.border.light }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.border.light }}
                />
              </View>
              <View className="w-10" />
            </View>
          </Animated.View>

          {/* Title */}
          <Animated.View
            entering={FadeInDown.delay(200).duration(500)}
            className="px-5 pb-6"
          >
            <View className="flex-row items-center gap-3 mb-2">
              <Lightbulb size={24} color={colors.primary[600]} />
              <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                Step 2 of 4
              </Text>
            </View>
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
              Problem & Solution
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              What problem are you solving?
            </Text>
          </Animated.View>

          {/* Form */}
          <ScrollView
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <Animated.View entering={FadeInUp.delay(300).duration(500)} className="pb-8">
              {formContent}
            </Animated.View>
          </ScrollView>

          {/* Skip Button */}
          <SkipButton
            step="profile-details"
            nextRoute="/(onboarding)/serial-founder"
            label="Skip questionnaire"
            delay={500}
          />
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}
